import { useWallet } from '@/hooks/useWallet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Copy, ExternalLink, LogOut, Wallet, Loader2, ChevronDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { useState } from 'react';

interface WalletButtonProps {
  variant?: 'default' | 'outline' | 'ghost' | 'hero';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
  showDashboardLink?: boolean;
}

export function WalletButton({ 
  variant = 'default', 
  size = 'default',
  className,
  showDashboardLink = true
}: WalletButtonProps) {
  const { address, isConnected, isConnecting, connectors, connectWithConnector, disconnect } = useWallet();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const copyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      toast({
        title: 'Address copied',
        description: 'Wallet address copied to clipboard',
      });
    }
  };

  const handleConnect = () => {
    const injected = connectors.find((c) => c.id === 'injected');
    if (injected) {
      connectWithConnector(injected.id);
      return;
    }

    const first = connectors[0];
    if (first) {
      connectWithConnector(first.id);
    }
  };

  if (isConnected && address) {
    return (
      <div className="flex items-center gap-2">
        {showDashboardLink && (
          <Link to="/app">
            <Button variant="outline" size={size}>
              Dashboard
            </Button>
          </Link>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant={variant} size={size} className={className}>
              <Wallet className="w-4 h-4 mr-2" />
              {formatAddress(address)}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={copyAddress}>
              <Copy className="w-4 h-4 mr-2" />
              Copy Address
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a 
                href={`https://arbiscan.io/address/${address}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View on Explorer
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => disconnect()}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Disconnect
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  if (isConnecting) {
    return (
      <Button variant={variant} size={size} disabled className={className}>
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Connecting...
      </Button>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          onClick={connectors.length === 1 ? handleConnect : undefined}
          className={className}
        >
          <Wallet className="w-4 h-4 mr-2" />
          Connect Wallet
          {connectors.length > 1 && <ChevronDown className="w-4 h-4 ml-2" />}
        </Button>
      </DropdownMenuTrigger>
      {connectors.length > 1 && (
        <DropdownMenuContent align="end" className="w-56">
          {connectors.map((connector) => (
            <DropdownMenuItem
              key={connector.id}
              onClick={() => {
                connectWithConnector(connector.id);
                setIsOpen(false);
              }}
              className="cursor-pointer"
            >
              {connector.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}
